import { motion } from 'framer-motion';
import { ExternalLink, Droplets, BookOpen, Heart, Users, Shield } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const HERO_BG = '/assets/images/about.png';

const IMAGES = {
  rahvai: "https://images.unsplash.com/photo-1566853166891-3936c0191f60?w=800",
  seeal: "https://images.unsplash.com/photo-1732696683011-2f80bca1f38b?w=800",
  behavioral: "https://images.unsplash.com/photo-1758273240631-59d44c8f5b66?w=800"
};

export default function ImpactPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero */}
      <section
        className="relative pt-32 pb-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        data-testid="impact-hero"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#2D3E55]/85 to-[#1a2634]/85"></div>

        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center text-white max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Impact Ecosystem
            </h1>
            <p className="font-body text-lg text-white/80">
              A network of initiatives designed to address different dimensions of community need, 
              each contributing to our shared mission of strengthening communities and restoring hope.
            </p>
          </motion.div>
        </div>
      </section>

      {/* RAHVAI Section */}
      <section className="section-padding" data-testid="rahvai-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D3E55]/10 rounded-full text-[#2D3E55] font-body text-sm font-medium mb-6">
                <Heart size={16} className="text-[#2D3E55]" />
                Humanitarian Outreach
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-6">
                RAHVAI
              </h2>
              <p className="font-heading text-xl text-[#F15A24] mb-4">
                Rehabilitation and Hope for the Vulnerable in Africa Initiative
              </p>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                RAHVAI serves as the humanitarian outreach initiative supporting vulnerable communities 
                across Africa. Programs help strengthen community resilience and restore dignity.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: Droplets, text: 'Water access initiatives' },
                  { icon: Users, text: 'Community relief efforts' },
                  { icon: Heart, text: 'Empowerment programs' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2D3E55]/10 flex items-center justify-center">
                      <item.icon size={20} className="text-[#2D3E55]" />
                    </div>
                    <span className="font-body text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>

              <a href="https://www.rahvai.org" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#2D3E55] hover:bg-[#1a2634] text-white">
                  Visit RAHVAI
                  <ExternalLink size={16} className="ml-2" />
                </Button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={IMAGES.rahvai}
                alt="RAHVAI - African community support"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEEAL Africa Section */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="seeal-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 lg:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={IMAGES.seeal}
                alt="SEEAL Africa - Economic empowerment"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </motion.div>

            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#F15A24]/10 rounded-full text-[#F15A24] font-body text-sm font-medium mb-6">
                <Users size={16} className="text-[#F15A24]" />
                Economic Empowerment
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-6">
                SEEAL Africa
              </h2>
              <p className="font-heading text-xl text-[#F15A24] mb-4">
                Skills. Enterprise. Access. Livelihood.
              </p>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                SEEAL Africa focuses on economic empowerment by equipping youth and women with 
                practical skills, enterprise development opportunities, and access to resources 
                that support sustainable livelihoods.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Structured training cohorts',
                  'Mentorship programs',
                  'Skills to income transformation',
                  'Long-term economic stability support',
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#F15A24]"></div>
                    <span className="font-body text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <a href="https://www.seeal.africa" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F15A24] hover:bg-[#d64d1e] text-white">
                  Visit SEEAL Africa
                  <ExternalLink size={16} className="ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Behavioral Health Section */}
      <section className="section-padding" data-testid="behavioral-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#6EC1E4]/10 rounded-full text-[#6EC1E4] font-body text-sm font-medium mb-6">
                <Shield size={16} className="text-[#6EC1E4]" />
                Behavioral Health Support
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2D3E55] mb-6">
                Behavioral Health Navigation & Workforce Agency
              </h2>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                Supporting individuals and families navigating behavioral health systems while 
                strengthening the workforce that serves these communities.
              </p>

              <Card className="bg-[#6EC1E4]/5 border-[#6EC1E4]/20 mb-8">
                <CardContent className="p-6">
                  <h4 className="font-heading text-lg font-semibold text-[#2D3E55] mb-4">
                    Key Services
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      'Navigation assistance',
                      'Community guidance',
                      'Workforce development',
                      'Veteran support',
                    ].map((service, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#6EC1E4]/20 flex items-center justify-center">
                          <BookOpen size={12} className="text-[#6EC1E4]" />
                        </div>
                        <span className="font-body text-sm text-slate-700">{service}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <a href="https://www.behavioralhealthnavigation.org" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#6EC1E4] hover:bg-[#5ab0d3] text-white">
                  Visit BHNA
                  <ExternalLink size={16} className="ml-2" />
                </Button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={IMAGES.behavioral}
                alt="Behavioral Health Navigation"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}